import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import DisableIcon from '@material-ui/icons/HighlightOff';
import UserIcon from '@material-ui/icons/Person';
import CopyIcon from '@material-ui/icons/FileCopy';
import CreatedAtIcon from '@material-ui/icons/AccessTime';
import APIKeyIcon from '@material-ui/icons/Code';
import RegionIcon from '@material-ui/icons/Language';
import { CardItem } from 'grb-webui/components/Card';
import { copyToClipboard } from 'grb-webui/editors/ObjectIdEditor';
import { ActionButtons } from 'grb-webui/components/Actions';
import './APIKeyCard.scss';

const IPIcon = () => <Typography variant="caption" className="grb-ip-icon">IP</Typography>;

const APIKeyCard = ({
  useApplicationName,
  useDisabled,
  useDescription,
  useLicense,
  useSameOwner = true,
  isSameOwner,
  getKey = k => k.key,
  getApplicationName = k => k.appName,
  getDescription = k => k.description,
  useUsage = true,
  useRegionUsage,
  noDownload,
  actions,
  rowButtons,
  rowMenuItems,
  ...cfg
 }) => {
  const useOwner = cfg.useOwner === undefined ? Boolean(cfg.getOwner) : cfg.useOwner;
  const getOwner = useOwner ? (cfg.getOwner || (k => (k.user && k.user.email))) : undefined;
  const actionOptions = { ...(cfg.actionOptions || {}), objectProp: 'object' }; // The key object will provided with the object property

  const HorizAPIKeyCard = (props) => {
    const {
      object: apiKey, // The object property contains the used in buttons and menu items
      authService,
      viewService,
      className,
      id,
      newlyCreated,
    } = props;
    const [LicenseIcon] = useState(() => viewService.getIcon('Licenses'));
    const [RowButtons] = useState(() => ActionButtons(actions, rowButtons, actionOptions));
    // const { menuProps, onOpenMenu, Menu } = useMenu(actions, rowMenuItems, actionOptions);
    const handleCopy = () => {
      copyToClipboard(getKey(apiKey));
    };

    const sameOwner = !useOwner || !useSameOwner
      || (isSameOwner ? isSameOwner(apiKey) : (authService.getProfile().id === apiKey.ownerId));
    const desc = useDescription && getDescription(apiKey);
    const appName = useApplicationName && getApplicationName(apiKey);

    return (
      <Paper
        className={classnames('grb-api-key-horiz-card',
          !sameOwner && 'grb-api-key-card-different-owner',
          className,
          newlyCreated && 'grb-card-newly-created',
          apiKey.disabled && 'grb-api-key-card-disabled')}
        id={id}
        elevation={3}
      >
        <header />
        <section className="grb-api-key-horiz-main-section">
          {/* Top row */}
          <div className="grb-api-key-horiz-card-row">
            <div className="grb-api-key-card-main-body grb-row-top">
              <div className="grb-column-left">
                <div className="grb-api-key-card-main-row">
                  <CardItem
                    icon={APIKeyIcon}
                    title={getKey(apiKey)}
                    value={`${appName || ''}${(appName && desc) ? ' - ' : ''}${desc || ''}`}
                    className="grb-api-key-card-access-id"
                    renderButtons={() => (
                      <Tooltip title="Copy access ID">
                        <IconButton aria-label="copy" onClick={handleCopy} id={`copy-api-key-${id}`}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="grb-flex" />
            <div className="grb-api-key-buttons">
              <RowButtons {...props} />
            </div>
          </div>
          {/* Second informative row */}
          <div className="grb-api-key-horiz-card-row grb-api-key-horiz-card-row-info">
            {useOwner && (
              <CardItem
                icon={UserIcon}
                title="Owner"
                value={getOwner(apiKey) || ''}
                className="grb-api-key-card-owner"
              />
            )}
            {!!apiKey.licenseId && (
              <CardItem
                icon={LicenseIcon}
                title="License"
                value={apiKey.licenseId || ''}
                className="grb-api-key-card-license-id"
              />
            )}
            <CardItem
              icon={CreatedAtIcon}
              title="Created At"
              className="grb-api-key-card-created-at"
              value={`${new Date(apiKey.createdAt).toLocaleDateString()} ${new Date(apiKey.createdAt).toLocaleTimeString()}`}
            />
            {useUsage && (
              <React.Fragment>
                <div className="grb-vertical-separator" />
                <CardItem
                  icon={CreatedAtIcon}
                  title="Last used"
                  className="grb-api-key-card-last-used-at"
                  value={!apiKey.usedAt ? 'N/A' : `${moment(new Date(apiKey.usedAt)).fromNow()}`}
                />
                <CardItem
                  icon={IPIcon}
                  title="With IP"
                  value={apiKey.usedIP || 'N/A'}
                />
                {useRegionUsage && (
                  <CardItem
                    icon={RegionIcon}
                    title="In region"
                    className="grb-api-key-card-last-used-region"
                    value={apiKey.usedRegion || 'N/A'}
                  />
                )}
              </React.Fragment>
            )}
            {useDisabled && apiKey.disabled && (
              <React.Fragment>
                <div className="grb-vertical-separator" />
                <CardItem
                  icon={DisableIcon}
                  title="Disabled"
                  className="grb-api-key-card-disabled"
                />
              </React.Fragment>
            )}
          </div>
        </section>
      </Paper>
    );
  };

  HorizAPIKeyCard.propTypes = {
    object: PropTypes.shape({
      key: PropTypes.string,
      user: PropTypes.shape({
        email: PropTypes.string,
      }),
      createdAt: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      usedAt: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      usedIP: PropTypes.string,
    }).isRequired,
    id: PropTypes.string,
    authService: PropTypes.shape({
      getProfile: PropTypes.func,
    }).isRequired,
    viewService: PropTypes.shape({
      getIcon: PropTypes.func,
    }).isRequired,
    apiKeyService: PropTypes.shape({
      getDownloadLicenseURL: PropTypes.func,
    }).isRequired,
    className: PropTypes.string,
    newlyCreated: PropTypes.bool,
  };

  HorizAPIKeyCard.defaultProps = {
    id: undefined,
    className: undefined,
    newlyCreated: false,
  };
  return HorizAPIKeyCard;
};

export default APIKeyCard;
